// Dashboard chart card: yearly user-registration trend as a line chart, with
// year navigation.
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Box, Divider, Flex, Spinner, Text, useColorModeValue, useToken } from "@chakra-ui/react";
import { MdPersonAddAlt1 } from "react-icons/md";
import Card from "horizon/components/card/Card";
import CardHeader from "../shared/CardHeader";
import ChartLegend from "../shared/ChartLegend";
import YearNavFooter from "../shared/YearNavFooter";
import SvgLineChart, { MONTH_LABELS } from "../shared/SvgLineChart";

/**
 * Displays the monthly user-registration trend for a given year and lets the
 * admin navigate between years.
 * @returns {React.ReactElement}
 */
export default function UserBehaviorsCard() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [values, setValues] = useState([]);
  const [brand] = useToken("colors", ["brand.500"]);

  const load = useCallback(async (targetYear) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`/admin/users/years_stat/${targetYear}`);
      setValues(data || []);
    } catch (error) {
      console.error("Error fetching user registration stats:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const changeYear = useCallback((delta) => {
    const next = year + delta;
    setYear(next);
    load(next);
  }, [year, load]);

  useEffect(() => {
    load(year);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const subColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  const datasets = [{ label: "Inscriptions", values, color: brand }];
  return (
    <Card h="100%">
      <CardHeader icon={MdPersonAddAlt1} title="Inscriptions" accent="green.500"
        right={<Text fontSize="xs" color={subColor}>Année {year}</Text>} />
      <Divider mb="8px" />
      <Box position="relative">
        {loading && (
          <Flex position="absolute" inset="0" align="center" justify="center" zIndex="2">
            <Spinner color="green.500" />
          </Flex>
        )}
        <SvgLineChart datasets={datasets} labels={MONTH_LABELS} height="300px" />
      </Box>
      <ChartLegend items={[{ color: brand, label: "Inscriptions" }]} />
      <YearNavFooter loading={loading} onPrev={() => changeYear(-1)} onNext={() => changeYear(1)}
        onRefresh={() => load(year)} />
    </Card>
  );
}
